/**
 * Bounded renderer-loss recovery guard (Plan 32 Phase 3).
 *
 * Covers NOTIFIED context/device loss (`engine.onContextLostObservable` and
 * `engine.onContextRestoredObservable`).
 *
 * When loss is notified:
 * - Immediately freezes the simulation.
 * - Immediately pauses audio directly from the callback (Babylon skips application frame
 *   callbacks while `_contextWasLost`, and `music.ts` runs an independent scheduler).
 * - Shows a recovery overlay (`#renderer-lost`).
 *
 * If restore arrives within the bounded window:
 * - Hides the overlay, unpauses audio, resumes simulation (caller resets wall-clock delta
 *   and simTime to prevent catch-up jumps).
 *
 * If the restore window lapses:
 * - Invokes the persistFailureFlag callback exactly once (the CALLER owns the policy — e.g.
 *   persisting gfx crash memory so the next manual reload boots WebGL2).
 * - Switches overlay to a manual-reload message.
 * - NEVER auto-reloads (bounds loops).
 * - Late restore past the lapse does NOT silently resume — neither when the timer already
 *   dispatched, nor when the restore event races in after the deadline but before the
 *   timer dispatches (the restore path re-checks the deadline itself).
 */

export type RendererLossOverlayMode = 'recovering' | 'failed';

export interface RendererLossCallbacks {
  pauseSimulation: () => void;
  resumeSimulation: () => void;
  pauseAudio: () => void;
  resumeAudio: () => void;
  showOverlay: (mode: RendererLossOverlayMode) => void;
  hideOverlay: () => void;
  persistFailureFlag: (reason: string) => void;
}

export interface ObservableLike<T = unknown> {
  add(callback: (eventData: T) => void): unknown;
  remove?(observer: unknown): unknown;
}

export interface EngineObservables {
  onContextLostObservable: ObservableLike<unknown>;
  onContextRestoredObservable: ObservableLike<unknown>;
}

export const DEFAULT_RESTORE_WINDOW_MS = 5000;

export class RendererLossGuard {
  private currentState: 'idle' | 'lost' | 'lapsed' = 'idle';
  private timerId: ReturnType<typeof setTimeout> | number | null = null;
  private lostAt = 0;
  private readonly lostObserver: unknown;
  private readonly restoredObserver: unknown;

  constructor(
    private readonly engine: EngineObservables,
    private readonly callbacks: RendererLossCallbacks,
    public readonly restoreWindowMs: number = DEFAULT_RESTORE_WINDOW_MS,
    private readonly now: () => number = () => performance.now(),
    private readonly timerFns: {
      setTimeout: (fn: () => void, ms: number) => any;
      clearTimeout: (id: any) => void;
    } = {
      setTimeout: (fn, ms) => setTimeout(fn, ms),
      clearTimeout: (id) => clearTimeout(id),
    },
  ) {
    this.lostObserver = this.engine.onContextLostObservable.add(() => this.handleContextLost());
    this.restoredObserver = this.engine.onContextRestoredObservable.add(() => this.handleContextRestored());
  }

  /** Read-only introspection (tests + diagnostics); the guard's contract is its callbacks. */
  public get state(): 'idle' | 'lost' | 'lapsed' {
    return this.currentState;
  }

  public handleContextLost(): void {
    // Idempotent: ignore repeated loss events when already lost or lapsed.
    if (this.currentState !== 'idle') {
      return;
    }
    this.currentState = 'lost';
    this.lostAt = this.now();

    this.callbacks.pauseSimulation();
    this.callbacks.pauseAudio();
    this.callbacks.showOverlay('recovering');

    this.timerId = this.timerFns.setTimeout(() => {
      this.handleWindowLapsed();
    }, this.restoreWindowMs);
  }

  public handleWindowLapsed(): void {
    if (this.currentState !== 'lost') {
      return;
    }
    this.currentState = 'lapsed';
    if (this.timerId !== null) {
      this.timerFns.clearTimeout(this.timerId);
      this.timerId = null;
    }

    // Persist failure flag exactly once and prompt for manual reload.
    // NEVER auto-reload to avoid loop traps.
    this.callbacks.persistFailureFlag('renderer-lost-timeout');
    this.callbacks.showOverlay('failed');
  }

  public handleContextRestored(): void {
    // Simplest safe behavior:
    // Once the restore window has lapsed, the failure flag has already been persisted
    // and the overlay shows a manual reload prompt. A late restore MUST NOT silently
    // resume past the lapse or hide the overlay, as doing so would violate crash flag
    // semantics and could leave the game running in a corrupted/unstable state behind
    // the failure notice.
    if (this.currentState === 'lapsed') {
      return;
    }

    if (this.currentState === 'lost') {
      // Restore raced the timer: the deadline itself decides, not the dispatch order.
      // A restore event arriving after the deadline (but before the timeout callback
      // ran) must lapse, not resume (impl-gate finding: the old code resumed here).
      if (this.now() - this.lostAt >= this.restoreWindowMs) {
        this.handleWindowLapsed();
        return;
      }
      this.resumeInternal();
    }
  }

  private resumeInternal(): void {
    if (this.currentState !== 'lost') {
      return;
    }
    if (this.timerId !== null) {
      this.timerFns.clearTimeout(this.timerId);
      this.timerId = null;
    }
    this.currentState = 'idle';

    this.callbacks.hideOverlay();
    this.callbacks.resumeSimulation();
    this.callbacks.resumeAudio();
  }

  public dispose(): void {
    if (this.timerId !== null) {
      this.timerFns.clearTimeout(this.timerId);
      this.timerId = null;
    }
    if (this.engine.onContextLostObservable?.remove) {
      this.engine.onContextLostObservable.remove(this.lostObserver);
    }
    if (this.engine.onContextRestoredObservable?.remove) {
      this.engine.onContextRestoredObservable.remove(this.restoredObserver);
    }
  }
}
